import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@/components/ui/Button";
import { getV2Error } from "@/services/api/v2";
import {
  MatchEligibilityPlayer,
  matchKeys,
  matchesV2API,
  MatchSquadMember,
} from "@/services/api/matchesV2";
import SectionQuery from "./SectionQuery";
import { useMatchDirtyGuard } from "@/context/MatchRuntimeContext";
import { refreshMatchCapabilitiesOnForbidden } from "../refreshMatchCapabilities";

const MatchSquadSection: React.FC<{
  workspaceId: string;
  id: string;
  canEdit: boolean;
}> = ({ workspaceId, id, canEdit }) => {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: matchKeys.section(workspaceId, "manual", id, "squad"),
    queryFn: () => matchesV2API.squad(workspaceId, id),
  });
  const eligibility = useQuery({
    queryKey: matchKeys.section(workspaceId, "manual", id, "eligibility"),
    queryFn: () => matchesV2API.eligibility(workspaceId, id),
  });
  const [members, setMembers] = useState<MatchSquadMember[]>([]);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState("");
  useMatchDirtyGuard(`match-squad-${id}`, dirty, "squad changes");
  useEffect(() => {
    if (!dirty && query.data) setMembers(query.data.data);
  }, [dirty, query.data]);
  const save = useMutation({
    mutationFn: () =>
      matchesV2API.saveSquad(
        workspaceId,
        id,
        members.map((member) => ({
          playerId: member.player.id,
          role: member.role,
          shirtNumber: member.shirtNumber,
        })),
      ),
    onSuccess: (data) => {
      client.setQueryData(
        matchKeys.section(workspaceId, "manual", id, "squad"),
        data,
      );
      setMembers(data.data);
      setDirty(false);
      setMessage("Squad saved.");
      void client.invalidateQueries({
        queryKey: matchKeys.section(workspaceId, "manual", id, "eligibility"),
      });
    },
    onError: (error) => {
      refreshMatchCapabilitiesOnForbidden(error, client, workspaceId, id);
      setMessage(getV2Error(error).message);
    },
  });
  const add = (playerId: string) => {
    const player = eligibility.data?.find(
      (value: MatchEligibilityPlayer) => value.id === playerId,
    );
    if (
      !player ||
      members.some((value) => value.player.id === playerId) ||
      player.availability === "ineligible"
    )
      return;
    if (
      player.availability === "doubtful" &&
      !window.confirm(`${player.name} is doubtful. Add them to the squad?`)
    )
      return;
    setMembers((value) => [
      ...value,
      {
        player: { id: player.id, name: player.name },
        role: "reserve",
        shirtNumber: null,
        position: null,
      },
    ]);
    setDirty(true);
  };
  return (
    <SectionQuery title="Fixture squad" query={query}>
      <div className="space-y-4">
        {canEdit && (
          <label className="block max-w-md text-sm font-medium">
            Add eligible player
            <select
              className="mt-1 min-h-11 w-full rounded-md border bg-white px-3 dark:border-gray-700 dark:bg-gray-900"
              value=""
              onChange={(event) => add(event.target.value)}
            >
              <option value="">Select player...</option>
              {eligibility.data?.map((player: MatchEligibilityPlayer) => (
                <option
                  key={player.id}
                  value={player.id}
                  disabled={
                    player.availability === "ineligible" ||
                    members.some((value) => value.player.id === player.id)
                  }
                >
                  {player.name} ({player.availability})
                </option>
              ))}
            </select>
          </label>
        )}
        <ul className="space-y-3">
          {members.map((member, index) => (
            <li
              key={member.player.id}
              className="grid gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800 sm:grid-cols-[1fr_10rem_8rem_auto] sm:items-end"
            >
              <p className="font-semibold">
                {member.player.name}
                <span className="block text-sm font-normal text-gray-500">
                  {member.position || "Position not set"}
                </span>
              </p>
              <label className="text-sm">
                Role
                <select
                  disabled={!canEdit}
                  value={member.role}
                  onChange={(event) => {
                    const next = [...members];
                    next[index] = {
                      ...member,
                      role: event.target.value as MatchSquadMember["role"],
                    };
                    setMembers(next);
                    setDirty(true);
                  }}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                >
                  <option value="starter">Starter</option>
                  <option value="bench">Bench</option>
                  <option value="reserve">Reserve</option>
                </select>
              </label>
              <label className="text-sm">
                Shirt number
                <input
                  disabled={!canEdit}
                  type="number"
                  min="1"
                  max="99"
                  value={member.shirtNumber ?? ""}
                  onChange={(event) => {
                    const next = [...members];
                    next[index] = {
                      ...member,
                      shirtNumber: event.target.value
                        ? Number(event.target.value)
                        : null,
                    };
                    setMembers(next);
                    setDirty(true);
                  }}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                />
              </label>
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setMembers((value) =>
                      value.filter(
                        (item) => item.player.id !== member.player.id,
                      ),
                    );
                    setDirty(true);
                  }}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
        {members.length === 0 && <p>No players selected.</p>}
        {canEdit && (
          <div className="flex items-center gap-3">
            <Button
              loading={save.isPending}
              loadingLabel="Saving fixture squad"
              disabled={!dirty}
              onClick={() => save.mutate()}
            >
              Save squad
            </Button>
            {dirty && (
              <span className="text-sm text-amber-700 dark:text-amber-300">
                Unsaved changes
              </span>
            )}
          </div>
        )}
        {message && (
          <p
            role={save.isError ? "alert" : "status"}
            className={
              save.isError
                ? "text-red-700 dark:text-red-300"
                : "text-green-700 dark:text-green-300"
            }
          >
            {message}
          </p>
        )}
      </div>
    </SectionQuery>
  );
};
export default MatchSquadSection;
